import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { Card } from './ui/card';
import { useNavigate } from 'react-router-dom';
import { sanitizeUrl } from '@/lib/utils';
import { nip19 } from 'nostr-tools';
import { getThumbnailUrl, extractThumbnailFromImeta } from '@/lib/videoMetadata';

interface VideoCardProps {
  event: NostrEvent;
}

export function VideoCard({ event }: VideoCardProps) {
  const navigate = useNavigate();
  const author = useAuthor(event.pubkey);
  const metadata = author.data?.metadata;

  // Extract tags
  const titleTag = event.tags.find(([name]) => name === 'title');
  const title = titleTag?.[1] ?? 'Untitled Video';

  const imetaTag = event.tags.find(([name]) => name === 'imeta');
  const imetaContent = imetaTag?.[1] ?? '';
  const videoUrl = imetaContent.match(/url\s+(.+?)(?:\s+image\s+|\s+m\s+|\s*$)/)?.[1];
  const imageUrl = extractThumbnailFromImeta(imetaContent) || getThumbnailUrl(videoUrl || '');

  const tags = event.tags.filter(([name]) => name === 't').map(([, tag]) => tag);

  const displayName = metadata?.name ?? genUserName(event.pubkey);
  const profileImage = metadata?.picture;

  const handleClick = () => {
    // Encode as note1 for NIP-19 routing
    const noteId = nip19.noteEncode(event.id);
    navigate(`/${noteId}`);
  };

  return (
    <Card
      className="overflow-hidden cursor-pointer hover:shadow-lg hover:shadow-pink-500/10 transition-all duration-300 group border-border/50 hover:border-pink-500/50"
      onClick={handleClick}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-muted overflow-hidden">
        {imageUrl ? (
          <img
            src={sanitizeUrl(imageUrl)}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/50">
            <svg className="w-12 h-12 text-muted-foreground/50" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        )}
        
        {/* Play overlay */}
        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-12 h-12 rounded-full bg-pink-500/90 flex items-center justify-center shadow-lg">
            <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <h3 className="font-semibold text-sm line-clamp-2 leading-snug">{title}</h3>

        {/* Author */}
        <div className="flex items-center space-x-2">
          {profileImage ? (
            <img
              src={sanitizeUrl(profileImage)}
              alt={displayName}
              className="w-6 h-6 rounded-full"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-500 to-cyan-400" />
          )}
          <span className="text-xs text-muted-foreground">{displayName}</span>
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 rounded-full bg-pink-500/10 text-pink-500 border border-pink-500/20"
              >
                #{tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className="text-xs text-muted-foreground">+{tags.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}